/**
 * ACTIVITY-CORE — feed name resolver backed by LOCAL tables.
 *
 * The history patch only carries a reliable name in created/deleted snapshots
 * (a `checked` toggle is only `{checked}`), so we resolve the name by
 * (entity_type, entity_id) against the account's SQLite database:
 *   - shopping_item.name, recipe.name, pantry_item.name (direct)
 *   - meal_plan_entry → its recipe_id → recipe.name (indirect)
 *
 * We query ALL recipes (including those in the trash) so that
 * "moved {name} to the trash" still resolves the name. This is a handful of
 * rows per home: cheap and reactive.
 */
import { type Accessor, createMemo } from "solid-js";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import {
  meal_plan_entry,
  pantry_item,
  type PowerSyncDrizzleDB,
  recipe,
  shopping_item,
} from "@mealplan/powersync";
import { createPowerSyncQuery } from "@mealplan/shared/solid";
import type { EntityNameResolver } from "./activity-text";

interface UseActivityNamesArgs {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
}

/**
 * Local keys (unique id↔query) for the name resolver. They do not live in the
 * shared `queryKeys` factory because they are internal to this web layer; the
 * `["activity","names",…]` prefix guarantees that they do not collide with anything.
 */
const nameKeys = {
  recipe: (userId: string | undefined) => ["activity", "names", "recipe", userId] as const,
  shopping: (userId: string | undefined) => ["activity", "names", "shopping_item", userId] as const,
  pantry: (userId: string | undefined) => ["activity", "names", "pantry_item", userId] as const,
  entries: (userId: string | undefined) =>
    ["activity", "names", "meal_plan_entry", userId] as const,
};

interface IdName {
  id: string;
  name: string | null;
}
interface EntryRecipe {
  id: string;
  recipe_id: string | null;
}

function toMap(rows: IdName[] | undefined): Map<string, string> {
  const map = new Map<string, string>();
  for (const row of rows ?? []) {
    if (row.name && row.name.trim().length > 0) {
      map.set(row.id, row.name);
    }
  }
  return map;
}

export function useActivityNameResolver(args: UseActivityNamesArgs): Accessor<EntityNameResolver> {
  const enabled = () => Boolean(args.drizzle());

  const recipes = createPowerSyncQuery<IdName>({
    queryKey: () => nameKeys.recipe(args.userId()),
    db: args.db,
    queryFn: () => {
      const db = args.drizzle();
      return db ? db.select({ id: recipe.id, name: recipe.name }).from(recipe) : null;
    },
    enabled,
  });

  const shopping = createPowerSyncQuery<IdName>({
    queryKey: () => nameKeys.shopping(args.userId()),
    db: args.db,
    queryFn: () => {
      const db = args.drizzle();
      return db
        ? db.select({ id: shopping_item.id, name: shopping_item.name }).from(shopping_item)
        : null;
    },
    enabled,
  });

  const pantry = createPowerSyncQuery<IdName>({
    queryKey: () => nameKeys.pantry(args.userId()),
    db: args.db,
    queryFn: () => {
      const db = args.drizzle();
      return db
        ? db.select({ id: pantry_item.id, name: pantry_item.name }).from(pantry_item)
        : null;
    },
    enabled,
  });

  const entries = createPowerSyncQuery<EntryRecipe>({
    queryKey: () => nameKeys.entries(args.userId()),
    db: args.db,
    queryFn: () => {
      const db = args.drizzle();
      return db
        ? db
            .select({ id: meal_plan_entry.id, recipe_id: meal_plan_entry.recipe_id })
            .from(meal_plan_entry)
        : null;
    },
    enabled,
  });

  const recipeNames = createMemo(() => toMap(recipes.data));
  const shoppingNames = createMemo(() => toMap(shopping.data));
  const pantryNames = createMemo(() => toMap(pantry.data));
  const entryRecipe = createMemo(() => {
    const map = new Map<string, string>();
    for (const row of entries.data ?? []) {
      if (row.recipe_id) {
        map.set(row.id, row.recipe_id);
      }
    }
    return map;
  });

  const resolver = createMemo<EntityNameResolver>(() => {
    // oxlint-disable-next-line solid/reactivity -- intended snapshot: this memo re-runs when any source changes, so the returned resolver always closes over the current maps
    const recipesMap = recipeNames();
    // oxlint-disable-next-line solid/reactivity -- intended snapshot: this memo re-runs when any source changes, so the returned resolver always closes over the current maps
    const shoppingMap = shoppingNames();
    // oxlint-disable-next-line solid/reactivity -- intended snapshot: this memo re-runs when any source changes, so the returned resolver always closes over the current maps
    const pantryMap = pantryNames();
    // oxlint-disable-next-line solid/reactivity -- intended snapshot: this memo re-runs when any source changes, so the returned resolver always closes over the current maps
    const entriesMap = entryRecipe();
    return (entityType, entityId) => {
      if (!entityType || !entityId) {
        return null;
      }
      switch (entityType) {
        case "recipe": {
          return recipesMap.get(entityId) ?? null;
        }
        case "shopping_item": {
          return shoppingMap.get(entityId) ?? null;
        }
        case "pantry_item": {
          return pantryMap.get(entityId) ?? null;
        }
        case "meal_plan_entry": {
          const recipeId = entriesMap.get(entityId);
          return recipeId ? (recipesMap.get(recipeId) ?? null) : null;
        }
        default: {
          return null;
        }
      }
    };
  });
  return resolver;
}
